/* eslint-disable max-lines */
import React, {
 useState,useContext,useEffect
} from 'react';
import {
 Card, CardContent
} from 'shared/components/ui/card';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Skeleton
} from 'shared/components/ui/skeleton';
import {
 Dialog, DialogContent
} from 'shared/components/ui/dialog';
import {
 Database, Plus, Search
} from 'lucide-react';
import {
 ConnectionForm
} from './ConnectionForm';
import ConnectionCard from './Components/ConnectionCard';
import {
 Connection
} from './Components/Types/ConnectionTypes';
import {
 connectionService
} from 'shared/api/services/BEX';
import {
 Context
} from 'shared/context/GlobalState';
import AlertModal from 'shared/components/Alertmodal';
import {
 useNavigate, useLocation
} from 'react-router-dom';

interface ConnectionListProps {
  connections: Connection[];
  isLoading: boolean;
  onConnectionSaved: (data: any) => void;
  onDelete: (connection_id: string) => void;
  onBulkDelete: (connection_ids: string[]) => void; // ✅ new
  setModelProps: React.Dispatch<React.SetStateAction<{ open: boolean; data: Connection }>>;
  handleConnectionDeleted: (connection_id: string) => void;
  onNewConnection: () => void;
}

export const ConnectionList: React.FC<ConnectionListProps> = ({
    connections = [],
    isLoading,
    onConnectionSaved,
    // onDelete,
    setModelProps,
    // handleConnectionDeleted,
    onBulkDelete,
}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filterType, setFilterType] = useState<string>('all');
    const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
    const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [bulkTesting, setBulkTesting] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [delBulkLoading, setDelBulkLoading] = useState<boolean>(false);
    const [reloadConnections, setReloadConnections] = useState(false);
    const {
     addNewNotifcation
    } = useContext(Context);

  const navigate = useNavigate();
  const location = useLocation();
//   const {
//   appId
// } = useParams<{ appId: string }>();

  const handleEditConnection = (connection: Connection) => {
    setEditingConnection(connection);
    setIsFormOpen(true);
  };

  const handleFormSave = (savedConnection: Connection) => {
    onConnectionSaved(savedConnection);
    setIsFormOpen(false);
  };

    const filteredConnections = (connections || []).filter((conn) => {
        const name = conn.connection_name || '';
        const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterType === 'all' || conn.source_type === filterType;
        return matchesSearch && matchesFilter;
    });

    const handleSelect = (connection_id: string, checked: boolean) => {
        setSelectedIds(prev => checked ? [...prev, connection_id] : prev.filter(id => id !== connection_id));
    };

    const handleBulkTest = async () => {
        setBulkTesting(true);
        try {
            if (selectedIds.length === 0) return;
            const firstConn = filteredConnections.find(c => c.connection_id === selectedIds[0] || c.connection_id === selectedIds[0]);
            if (!firstConn) return;
            await connectionService.testBulkConnections(firstConn.orgId, firstConn.appId, selectedIds);
            addNewNotifcation('Bulk test completed successfully', 'success');
        } catch (error) {
            addNewNotifcation('Failed to test connections. Please try again.', 'error');
            console.error('Failed to test connections:', error);
        } finally {
            setBulkTesting(false);
        }
    };

    // Bulk delete confirmation modal state
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

    // Trigger modal on bulk delete button click
    const handleBulkDeleteClick = () => {
        setBulkDeleteModalOpen(true);
    };

    // Actual bulk delete logic, called after modal confirmation
    const handleBulkDeleteConfirmed = async () => {
        setBulkDeleting(true);
        setBulkDeleteModalOpen(false);
        try {
            // Call delete for each selected connection
            for (const id of selectedIds) {
                const conn = filteredConnections.find(c => c.connection_id === id || c.connection_id === id);
                if (conn) {
                    await connectionService.deleteConnection(conn.orgId, conn.appId, conn.connection_id || conn.connection_id);
                }
            }
            addNewNotifcation('Connections deleted successfully', 'success');
            setSelectedIds([]);
            onBulkDelete(selectedIds);
        } catch (error) {
            console.error('Failed to delete connections:', error);
            addNewNotifcation('Failed to delete connections. Please try again.', 'error');
        } finally {
            setDelBulkLoading(false);
            setBulkDeleting(false);
        }
    };
    // Reload connections after bulk delete
    useEffect(() => {
        setReloadConnections(false);
    }, [reloadConnections]);

const handleNewConnectionClick = () => {
    let page = 'new';
  navigate(`${location.pathname}/${page}`);
};

  return (
      <>
          <div className="space-y-8 p-8" style={{
 width: '100%'
}}>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-md border border-white/20 p-12 text-center space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                          <div className="p-3 bg-black rounded-2xl shadow-lg">
                              <Database className="h-8 w-8 text-white" />
                          </div>
                          <div>
                              <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 text-left">Connection Manager</h1>
                              <p className="text-black dark:text-neutral-400">Manage your database connections with style</p>
                          </div>
                      </div>
                      <Button
              onClick={handleNewConnectionClick}
              className="bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
            >
                          <Plus className="mr-2 h-4 w-4" />
                          New Connection
                      </Button>
                  </div>

                  {/* Search and Filter */}
                  <div className="flex gap-4">
                      <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                          <Input
                placeholder="Search connections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 focus-visible:ring-neutral-950 dark:focus-visible:ring-neutral-300"
              />
                      </div>
                      <Select value={filterType} onValueChange={setFilterType}>
                          <SelectTrigger className="w-48 focus:ring-neutral-950 dark:focus:ring-neutral-300">
                              <SelectValue placeholder="All Types" />
                          </SelectTrigger>
                          <SelectContent>
                              <SelectItem value="all">All Types</SelectItem>
                              <SelectItem value="postgresql">PostgreSQL</SelectItem>
                              <SelectItem value="mysql">MySQL</SelectItem>
                              <SelectItem value="sql-server">SQL Server</SelectItem>
                              <SelectItem value="mongodb">MongoDB</SelectItem>
                              <SelectItem value="redis">Redis</SelectItem>
                              <SelectItem value="csv">CSV</SelectItem>
                              <SelectItem value="excel">Excel</SelectItem>
                              <SelectItem value="json">JSON</SelectItem>
                              <SelectItem value="parquet">Parquet</SelectItem>
                          </SelectContent>
                      </Select>
                  </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/20 p-12 text-center space-y-8">
                  {/* Bulk Actions */}
                  {filteredConnections.length > 0 && (
                  <div className="flex gap-4 mb-4 items-center">
                      <Button
                                                onClick={handleBulkTest}
                                                disabled={selectedIds.length === 0 || bulkTesting}
                                                className="bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
                                            >
                          {bulkTesting ? 'Testing...' : 'Bulk Test'}
                      </Button>
                      <Button
                                                onClick={handleBulkDeleteClick}
                                                disabled={selectedIds.length === 0 || bulkDeleting}
                                                className="bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
                                            >
                          {bulkDeleting ? 'Deleting...' : 'Bulk Delete'}
                      </Button>
                      <span className="text-sm text-gray-500">{selectedIds.length} selected</span>
                  </div>
                                    )}
                  {/* Content */}
                  {isLoading ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {Array.from({
 length: 3
}).map((_, i) => (
    <Card key={i} className="bg-white dark:bg-neutral-900">
        <CardContent className="p-6">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <Skeleton className="h-9 w-9 rounded-lg" />
                    <div>
                        <Skeleton className="h-5 w-32 mb-2" />
                        <Skeleton className="h-4 w-24" />
                    </div>
                </div>
                <Skeleton className="h-8 w-8" />
            </div>
            <div className="mt-4 flex items-center justify-between">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-4 w-16" />
            </div>
        </CardContent>
    </Card>
              ))}
                      </div>
          ) : filteredConnections.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredConnections.map((connection) => (
                      <ConnectionCard
                                                key={connection?.connection_id || connection?.connectionId}
                                                connection={connection}
                                                onEdit={handleEditConnection}
                                                setModelProps={setModelProps}
                                                selected={selectedIds.includes(connection.connectionId || connection.connection_id)}
                                                onSelect={handleSelect}
                                            />
                                    ))}
              </div>
          ) : (
              <div className="flex flex-col items-center justify-center py-16">
                  <div className="p-6 bg-neutral-100 dark:bg-neutral-800 rounded-full mb-6">
                      <Database className="h-12 w-12 text-neutral-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                      {searchTerm || filterType !== 'all' ? 'No matching connections' : 'No connections yet'}
                  </h2>
                  <p className="text-black dark:text-neutral-400 text-center mb-8 max-w-md">
                      {searchTerm || filterType !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Create your first connection to get started with managing your databases'}
                  </p>
                  <Button
                onClick={handleNewConnectionClick}
                className="bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
              >
                      <Plus className="mr-2 h-4 w-4" />
                      Create Connection
                  </Button>
              </div>
          )}
              </div>
          </div>
          {editingConnection && (
          <Dialog
        open={isFormOpen}
        onOpenChange={(isOpen) => {
          setIsFormOpen(isOpen);
          if (!isOpen) {
            setEditingConnection(null);
          }
        }}
      >
              <DialogContent className="max-w-4xl h-[90vh] flex flex-col [&>button.absolute]:hidden">
                  <div className="flex-grow overflow-y-auto pr-6 -mr-6">
                      <ConnectionForm
              onConnectionSaved={handleFormSave}
              onCancel={() => setIsFormOpen(false)}
              editingConnection={editingConnection}
            />
                  </div>
              </DialogContent>
          </Dialog>)}
          {/* Bulk Delete Confirmation Modal */}
          <AlertModal
                        loading={delBulkLoading}
                        openModal={bulkDeleteModalOpen}
                        setopenModal={setBulkDeleteModalOpen}
                        onDelete={handleBulkDeleteConfirmed}
                        title="Delete Selected Connections"
                    />
      </>
  );
};
